import styled, { css } from 'styled-components'
import { Subject } from 'interfaces/Subject'
import MinimalistSubjectCard from './MinimalistSubjectCard'
import theme from 'stories/utils/theme'
import _useTranslation from 'lib/hooks/_useTranslation'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventSubjectsScrollListClick } from 'lib/constants/AnalyticsEventType'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

const Container = styled.div<{ subjectsCount: number; showLessText: string; showMoreText: string }>`
    position: relative;
    height: fit-content;
    border-top: 0;
    min-height: 95px;

    .subjects {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0px;
        .subject {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 20px;
            flex-direction: column;
        }
        padding-bottom: 30px;
    }
    [type='checkbox'] {
        position: absolute;
        left: -9999px;
    }
    label {
        background: transparent;
        display: block;
        width: 100%;
        height: 0px;
        border: 1px solid ${theme.colors.body2Black}22;
        cursor: pointer;
        position: absolute;
        text-align: center;
        bottom: 0;
        transition: top 3s cubic-bezier(0.44, 0.99, 0.48, 1);
        ${({ subjectsCount }) =>
            subjectsCount < 10
                ? css`
                      @media (min-width: 525px) {
                          display: none;
                      }
                  `
                : ''}
    }
    label:before,
    label:after {
        position: relative;
    }

    label:before {
        text-align: center;
        content: '${({ showMoreText }) => showMoreText}';
        bottom: 14px;
        margin: 0 auto;
        font-family: var(--font-inter);
        font-size: 15px;
        background-color: ${theme.colors.transparent};
        color: ${theme.colors.body1Black};
        padding: 0 20px;
    }

    [type='checkbox']:checked + label:before {
        content: '${({ showLessText }) => `${showLessText}`}';
        padding: 0 20px;
        margin: 0 auto;
        text-align: center;
    }

    label:after {
        -webkit-animation: sudo 3s linear infinite alternate;
        animation: sudo 3s linear infinite alternate;
    }

    @keyframes sudo {
        from {
            -webkit-transform: translateY(-2px);
            transform: translateY(-2px);
        }
        to {
            -webkit-transform: translateY(2px);
            transform: translateY(2px);
        }
    }

    input[type='checkbox'] ~ .subjects {
        width: 100%;
        overflow: hidden;
        min-height: 75px;
        max-height: 0;
        transition: max-height 0.45s cubic-bezier(0.44, 0.99, 0.48, 1);
    }
    [type='checkbox']:checked ~ .subjects {
        max-height: 750px;
        @media (max-width: 750px) {
            max-height: 1750px;
        }
    }

    [type='checkbox']:checked + label {
        top: 100%;
    }
`

interface Props {
    referrerScreen: string
    subjects: Subject[]
    subjectOnClickRedirect: (subject: Subject) => string
    isDarkTheme?: boolean
}

const SubjectScrollList = ({ referrerScreen, subjects, subjectOnClickRedirect, isDarkTheme }: Props) => {
    const [expandList, setExpandList] = useState(false)
    const { t: tCommon } = _useTranslation('common')
    const { asPath } = useRouter()

    useEffect(() => {
        setExpandList(false)
    }, [asPath])

    const onSubjectListCardClick = (subject: Subject) => {
        trackEvent(AnalyticsEventSubjectsScrollListClick, { referrerScreen, valueId: subject.id, value: subject.name })
    }

    const toggleList = () => setExpandList((prevExpand) => !prevExpand)

    if (!subjects?.length) return null

    return (
        <Container
            subjectsCount={subjects.length}
            showLessText={tCommon('common/showLessSubjectsButtonText')}
            showMoreText={tCommon('common/showMoreSubjectsButtonText')}
        >
            <input type="checkbox" id="check_id" checked={expandList} onClick={toggleList} />
            <label htmlFor="check_id" />
            <div className="subjects">
                {subjects.map((subject) => (
                    <span key={subject.id} onClick={() => onSubjectListCardClick(subject)}>
                        <MinimalistSubjectCard
                            to={subjectOnClickRedirect(subject)}
                            subject={subject}
                            isDarkTheme={isDarkTheme}
                        />
                    </span>
                ))}
            </div>
        </Container>
    )
}

export default SubjectScrollList
